import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { type SOPVersion, type SOP } from "../types/sop";
import { useSopStore } from "./useSopStore";

interface SopSelectionActions {
  setSelectedSOP: (sopId: string) => void;
  setSelectedVersion: (version: SOPVersion) => void;
  setLatestVersion: () => void;
  setBaseVersion: (version: SOPVersion | null) => void;
  setCompareVersion: (version: SOPVersion | null) => void;
  refreshSelectedSOP: () => void;

  // Selectors
  getSelectedSOP: () => SOP | null;
}

interface SopSelectionState extends SopSelectionActions {
  selectedSOP: string;
  selectedVersion: SOPVersion | null;
  latestVersion: SOPVersion | null;
  /** Version used as "Original" (left column) in document auditor diff */
  baseVersion: SOPVersion | null;
  /** Version used as "Updated" (right column) in document auditor diff */
  compareVersion: SOPVersion | null;
}

export const useSopSelectionStore = create<SopSelectionState>()(
  devtools(
    subscribeWithSelector(
      (set, get) => ({
        selectedSOP: "",
        selectedVersion: null,
        latestVersion: null,
        baseVersion: null,
        compareVersion: null,

        /**
         * Set the selected SOP and update versions. If the SOP is not found, clear version state.
         * @param sopId - The ID of the SOP to select
         */
        setSelectedSOP: (sopId: string) => {
          if (!sopId) {
            set({
              selectedVersion: null,
              latestVersion: null,
              baseVersion: null,
              compareVersion: null,
            });
            return;
          }

          set({ selectedSOP: sopId });

          const sop = useSopStore.getState().getSopById(sopId);
          if (sop) {
            const versions = sop.versions;
            const latest = sop.getLatestVersion();
            const latestIdx = latest ? versions.findIndex((v) => v.versionId === latest.versionId) : -1;
            const previousVersion =
              latestIdx > 0 ? versions[latestIdx - 1] ?? null : null;
            set({
              selectedVersion: latest || null,
              latestVersion: latest || null,
              compareVersion: latest || null,
              baseVersion: previousVersion ?? null,
            });
          } else {
            set({
              selectedVersion: null,
              latestVersion: null,
              baseVersion: null,
              compareVersion: null,
            });
          }
        },

        /**
         * Refresh the selected SOP data - useful after SOPs have been loaded (e.g. after fetchSOPById).
         */
        refreshSelectedSOP: () => {
          const { selectedSOP } = get();
          if (selectedSOP) {
            const sop = useSopStore.getState().getSopById(selectedSOP);
            if (sop) {
              const latest = sop.getLatestVersion();
              const versions = sop.versions;
              const latestIdx = latest ? versions.findIndex((v) => v.versionId === latest.versionId) : -1;
              const previousVersion =
                latestIdx > 0 ? versions[latestIdx - 1] ?? null : null;
              set({
                selectedVersion: latest || null,
                latestVersion: latest || null,
                compareVersion: latest || null,
                baseVersion: previousVersion ?? null,
              });
            }
          }
        },

        /**
         * Set the selected version (legacy; prefer baseVersion/compareVersion for auditor).
         */
        setSelectedVersion: (version: SOPVersion) => set({ selectedVersion: version }),

        /**
         * Set the latest version to the latest version of the selected SOP.
         */
        setLatestVersion: () => {
          const { selectedSOP } = get();
          const sop = useSopStore.getState().getSopById(selectedSOP);
          if (sop) {
            const latest = sop.getLatestVersion();
            set({ latestVersion: latest || null });
          } else {
            set({ latestVersion: null });
          }
        },

        setBaseVersion: (version: SOPVersion | null) => set({ baseVersion: version }),
        setCompareVersion: (version: SOPVersion | null) => set({ compareVersion: version }),

        // Selectors
        getSelectedSOP: () => {
          const { selectedSOP } = get();
          return useSopStore.getState().getSopById(selectedSOP);
        },
      })
    ),
    {
      name: 'sop-selection-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
); 